import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { showContactModal } from '../features/authSlice';

export default function DemosPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [demos, setDemos] = useState([]);
  const [selectedDemo, setSelectedDemo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Load demos from JSON
    fetch('/demos.json')
      .then(response => response.json())
      .then(data => {
        setDemos(data.demos);
        setIsLoading(false);
      })
      .catch(error => {
        console.error('Failed to load demos:', error);
        setIsLoading(false);
      });
  }, []);

  const handleBackToHome = () => {
    navigate('/');
  };

  const handlePlayVideo = (demo) => {
    setSelectedDemo(demo);
  };

  const handleCloseVideo = () => {
    setSelectedDemo(null);
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 overflow-hidden bg-[#0A0F1A] flex items-center justify-center">
        <div className="text-white text-xl">Loading demos...</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 overflow-hidden bg-[#0A0F1A]">
      {/* Animated gradient background with depth - exact match to LandingPage */}
      <div 
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(circle at 80% 20%, rgba(68, 194, 253, 0.12) 0%, transparent 50%),
            radial-gradient(circle at 20% 80%, rgba(35, 166, 213, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 50% 50%, rgba(68, 194, 253, 0.03) 0%, transparent 70%),
            linear-gradient(180deg, #0A0F1A 0%, #1C2837 100%)
          `,
        }}
      />
      
      {/* Animated mesh gradient overlay */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `
            repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(68, 194, 253, 0.03) 2px, rgba(68, 194, 253, 0.03) 4px),
            repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(68, 194, 253, 0.03) 2px, rgba(68, 194, 253, 0.03) 4px)
          `,
          backgroundSize: '100px 100px',
          animation: 'meshMove 20s linear infinite',
        }}
      />

      <style>{`
        @keyframes meshMove {
          0% { transform: translate(0, 0); }
          100% { transform: translate(100px, 100px); }
        }
        @keyframes glow {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 0.8; }
        }
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes buttonGlow {
          0%, 100% { box-shadow: 0 0 20px rgba(68, 194, 253, 0.3); }
          50% { box-shadow: 0 0 40px rgba(68, 194, 253, 0.6); }
        }
      `}</style>

      {/* Multiple animated glow orbs for depth - matching LandingPage */}
      <div 
        className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#44C2FD]/5 rounded-full blur-3xl"
        style={{
          animation: 'pulse 8s ease-in-out infinite',
        }}
      />
      <div 
        className="absolute bottom-1/3 right-1/3 w-64 h-64 bg-[#23A6D5]/5 rounded-full blur-3xl"
        style={{
          animation: 'pulse 10s ease-in-out infinite',
          animationDelay: '2s',
        }}
      />
      <div 
        className="absolute top-1/2 right-1/5 w-48 h-48 bg-[#44C2FD]/8 rounded-full blur-2xl"
        style={{
          animation: 'pulse 6s ease-in-out infinite',
          animationDelay: '4s',
        }}
      />

      {/* Content container with scroll */}
      <div className="relative h-full overflow-y-auto">
      {/* Header */}
      <div className="relative z-10 container mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-12">
          {/* Logo */}
          <div className="flex items-center gap-4">
            <img 
              src="/images/cara-white.png" 
              alt="Cara Logo" 
              className="h-20"
              style={{ 
                filter: 'brightness(1.15) drop-shadow(0 0 20px rgba(68, 194, 253, 0.5))' 
              }}
            />
            <h1 
              className="text-white"
              style={{
                fontSize: '30pt',
                fontWeight: 400,
                lineHeight: 1.1,
                fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, sans-serif',
                letterSpacing: '-0.02em',
                textShadow: '0 2px 40px rgba(68, 194, 253, 0.15)',
              }}
            >
              Demo Gallery
            </h1>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4">
            {/* Try It Now button */}
            <button
              onClick={handleBackToHome}
              className="px-6 lg:px-8 py-3 lg:py-3.5 bg-gradient-to-r from-[#44C2FD] to-[#23A6D5] text-white font-semibold rounded-lg hover:from-[#3AB0E8] hover:to-[#1E92BA] transition-all duration-300 uppercase relative group overflow-hidden"
              style={{
                fontSize: 'clamp(0.875rem, 2vw, 0.9375rem)',
                fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, sans-serif',
                letterSpacing: '0.05em',
                boxShadow: '0 4px 20px rgba(68, 194, 253, 0.3), 0 0 40px rgba(68, 194, 253, 0.1)',
              }}
            >
              <span className="relative z-10">TRY IT NOW</span>
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            </button>

            {/* Contact Us button */}
            <button
              onClick={() => dispatch(showContactModal())}
              className="px-6 lg:px-8 py-3 lg:py-3.5 bg-transparent text-white font-semibold rounded-lg border-2 border-[#44C2FD] hover:bg-[#44C2FD]/20 hover:border-[#3AB0E8] transition-all duration-300 uppercase relative group"
              style={{
                fontSize: 'clamp(0.875rem, 2vw, 0.9375rem)',
                fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, sans-serif',
                letterSpacing: '0.05em',
                boxShadow: '0 0 20px rgba(68, 194, 253, 0.15)',
              }}
            >
              <span className="relative z-10">CONTACT US</span>
              <div className="absolute inset-0 bg-gradient-to-r from-[#44C2FD]/0 via-[#44C2FD]/30 to-[#44C2FD]/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm" />
            </button>
          </div>
        </div>

        {/* Demos Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-12">
          {demos.map((demo) => (
            <div
              key={demo.id}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden hover:bg-white/10 transition-all duration-300 cursor-pointer group"
              onClick={() => handlePlayVideo(demo)}
            >
              {/* Video thumbnail */}
              <div className="relative aspect-video bg-black overflow-hidden">
                <video
                  src={demo.videoUrl}
                  className="w-full h-full object-cover"
                  preload="metadata"
                  muted
                />
                {/* Play overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#44C2FD]/20 to-[#23A6D5]/20 group-hover:from-[#44C2FD]/30 group-hover:to-[#23A6D5]/30 transition-all duration-300 flex items-center justify-center">
                  <span className="material-icons text-white text-6xl opacity-70 group-hover:opacity-100 group-hover:scale-110 transition-all duration-300">
                    play_circle_outline
                  </span>
                </div>
              </div>

              {/* Demo info */}
              <div className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-semibold text-[#44C2FD] uppercase tracking-wider">
                    {demo.category}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-white mb-2 line-clamp-2">
                  {demo.title}
                </h3>
                <p className="text-sm text-gray-400 mb-4 line-clamp-2">
                  {demo.description}
                </p>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span className="material-icons text-sm">business</span>
                  <span>{demo.company}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Video Modal */}
      {selectedDemo && (
        <div
          className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-6"
          onClick={handleCloseVideo}
        >
          <div
            className="relative max-w-6xl w-full bg-[#1a1f2e] rounded-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <div>
                <h2 className="text-2xl font-bold text-white mb-1">
                  {selectedDemo.title}
                </h2>
                <p className="text-sm text-gray-400">
                  {selectedDemo.description}
                </p>
              </div>
              <button
                onClick={handleCloseVideo}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <span className="material-icons text-white text-2xl">close</span>
              </button>
            </div>

            {/* Video player */}
            <div className="aspect-video bg-black">
              <video
                src={selectedDemo.videoUrl}
                controls
                autoPlay
                className="w-full h-full"
                style={{ maxHeight: 'calc(100vh - 200px)' }}
              >
                Your browser does not support the video tag.
              </video>
            </div>

            {/* Modal footer */}
            <div className="p-6 border-t border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="material-icons text-[#44C2FD]">business</span>
                <div>
                  <div className="text-sm text-gray-400">Company</div>
                  <div className="text-white font-semibold">{selectedDemo.company}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="material-icons text-[#44C2FD]">category</span>
                <div>
                  <div className="text-sm text-gray-400">Category</div>
                  <div className="text-white font-semibold">{selectedDemo.category}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add keyframe for gradient animation */}
      <style>{`
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
      `}</style>
      </div>
    </div>
  );
}
